import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const forgot = mode === "forgot";
  const request = mode === "request";
  const go = (m) => { setMode(m); setErr(""); setInfo(""); };
  if (user) return <Navigate to="/levels" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setBusy(true);
    try {
      if (forgot) {
        const { data } = await api.post("/auth/forgot-password", { email });
        setInfo(data.message);
      } else if (request) {
        const { data } = await api.post("/auth/request-access", { name, email, password });
        setInfo(data.message);
        setPassword("");
      } else {
        await login(email, password);
        nav("/levels");
      }
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain grid min-h-screen bg-transparent lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden border-r border-white/[0.06] lg:block">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(212,175,55,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(212,175,55,0.05)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_at_30%_40%,black,transparent_75%)]" />
        <div className="absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-4">
            <img data-testid="login-logo" src="/logo192.png" alt="Trenex Systems logo" className="h-12 w-12 rounded-lg ring-1 ring-white/10" />
            <div className="wordmark font-display text-xl font-bold uppercase tracking-[0.35em]">Trenex Systems</div>
          </div>
          <div className="space-y-4">
            {["Daily pivot levels on 12 markets", "Brent and WTI spread engine", "Paper first, live only when armed"].map((t, i) => (
              <div key={t} className="fade-up flex items-center gap-3 text-slate-400" style={{ animationDelay: `${i * 120}ms` }}>
                <span className="h-px w-10 bg-indigo-500/60" />
                <span className="text-sm tracking-wide">{t}</span>
              </div>
            ))}
          </div>
          <div className="num text-xs text-slate-600">Private access. Every account is approved by an administrator.</div>
        </div>
      </div>
      <div className="flex items-center px-6 py-16 sm:px-16">
        <form onSubmit={submit} className="panel-lux fade-up relative w-full max-w-sm space-y-5 rounded-2xl border border-white/[0.06] p-8 backdrop-blur-xl" data-testid="login-form">
          <div>
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <img data-testid="login-logo-mobile" src="/logo192.png" alt="Trenex Systems logo" className="h-10 w-10 rounded-lg ring-1 ring-white/10" />
              <div className="wordmark font-display text-lg font-bold uppercase tracking-[0.35em]">Trenex Systems</div>
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-100">{forgot ? "Reset password" : request ? "Request access" : "Sign in"}</h1>
            <p className="mt-1 text-sm text-slate-500">{forgot ? "We will email you a one-time reset link." : request ? "You can sign in once the administrator approves your request." : "Sign in with your approved account."}</p>
          </div>
          {request && (
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-slate-500">Name</label>
              <Input data-testid="request-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="h-11 bg-slate-950 border-slate-700" />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-slate-500">Email</label>
            <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-slate-950 border-slate-700" />
          </div>
          {!forgot && (
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-slate-500">Password</label>
              <Input data-testid="login-password-input" type="password" required minLength={request ? 8 : undefined} placeholder={request ? "8+ characters" : undefined} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-slate-950 border-slate-700" />
            </div>
          )}
          {err && <div data-testid="login-error" className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{err}</div>}
          {info && <div data-testid="login-info" className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">{info}</div>}
          <Button data-testid="login-submit-button" disabled={busy} className="btn-gold h-11 w-full">
            <Lock className="mr-2 h-4 w-4" />{busy ? "Please wait" : forgot ? "Send reset link" : request ? "Send request" : "Sign in"}
          </Button>
          <div className="flex justify-between gap-3">
            <button type="button" data-testid="login-forgot-toggle" onClick={() => go(mode === "signin" ? "forgot" : "signin")} className="text-sm text-slate-400 underline-offset-4 hover:text-slate-200 hover:underline">
              {mode === "signin" ? "Forgot password?" : "Back to sign in"}
            </button>
            {mode === "signin" && (
              <button type="button" data-testid="login-request-toggle" onClick={() => go("request")} className="text-sm text-[#EBD7A0] underline-offset-4 hover:underline">
                Request access
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
